import { useCallback, useState } from "react"
import { paymentService } from "@/features/payments"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { useAuthStore } from "@/stores/auth-store"
import { useQuizStore } from "@/stores/quiz-store"
import { useChat } from "@/features/chat"
import type { ChatPlan } from "@/features/chat"

interface UseCreditsPurchaseArgs {
  /** Subscription whose stored payment method is charged for the credit bundle. */
  userMainSubscriptionId: string | null
}

/**
 * Charges a star-credit bundle against the user's existing subscription payment
 * method (no card re-entry), mirroring the source `rePurchaseSubscription`.
 * Uses the project's upsell endpoint (`/subs/create-no-user` with subscription id).
 */
export function useCreditsPurchase({ userMainSubscriptionId }: UseCreditsPurchaseArgs) {
  const userEmail = useAuthStore((s) => s.user?.email)
  const quizEmail = useQuizStore((s) => s.answers.email) as string | undefined
  const email = userEmail || quizEmail || ""
  const { markCreditsPurchased } = useChat()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const purchase = useCallback(
    async (plan: ChatPlan, onSuccess: () => void) => {
      if (isLoading) return
      if (!userMainSubscriptionId) {
        setError("No active subscription to charge. Please try again later.")
        return
      }

      setIsLoading(true)
      setError(null)
      trackEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
        productId: plan.code,
        price: plan.unitAmount,
        currency: "USD",
        metadata: { kind: "credits", credits: plan.creditsCount },
      })

      try {
        const result = await paymentService.createUpsellSubscription({
          planId: plan.code,
          email,
          subscriptionId: userMainSubscriptionId,
        })
        trackEvent(ANALYTICS_EVENTS.PURCHASE, {
          productId: plan.code,
          transactionId: result.id,
          price: plan.unitAmount,
          currency: "USD",
          metadata: { kind: "credits" },
        })
        // Hide free-minutes promos after any stars top-up.
        markCreditsPurchased()
        onSuccess()
      } catch (err) {
        const message = err instanceof Error ? err.message : "An unexpected error occurred"
        setError(message)
        trackEvent(ANALYTICS_EVENTS.PAYMENT_FAILED, {
          productId: plan.code,
          metadata: { kind: "credits", error: message },
        })
      } finally {
        setIsLoading(false)
      }
    },
    [isLoading, userMainSubscriptionId, email, markCreditsPurchased],
  )

  return { purchase, isLoading, error, clearError: () => setError(null) }
}
